import React from 'react';

import { RiAddLine, RiDeleteBinLine } from '@remixicon/react';

import { Button } from '@/design-system/button';
import { Field } from '@/design-system/field';
import { Hint } from '@/design-system/hint';
import { IconButton } from '@/design-system/icon-button';
import { Input, InputRoot } from '@/design-system/input';
import { Label } from '@/design-system/label';
import { ToggleGroup, ToggleGroupItem } from '@/design-system/toggle-group';

import { UJP_COPY } from '../copy';
import MoneyInput from './MoneyInput';
import {
  blankBodyRow,
  blankTierRow,
  type BodyRateRow,
  type SharesDraft,
  type TierRow,
} from './tariffForm';

/**
 * Small row editors shared by the tariff-config and ring modals (CR-4b). Each
 * is controlled (rows in, rows out) and keyboard-reachable: every input has a
 * label / aria-label, every remove is an `IconButton` with an aria-label.
 */

const t = UJP_COPY.tariffs;

const eyebrow =
  'text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600';

// ─────────────────────────────────────────────────────────────────────────────
// Per-body override rows: unit type + rate
// ─────────────────────────────────────────────────────────────────────────────

export interface BodyRateEditorProps {
  /** Distinguishes the single / multi editors in ids + aria-labels. */
  idPrefix: string;
  label: string;
  rows: BodyRateRow[];
  onChange: (rows: BodyRateRow[]) => void;
  disabled?: boolean;
  hint?: string;
}

export const BodyRateEditor: React.FC<BodyRateEditorProps> = ({
  idPrefix,
  label,
  rows,
  onChange,
  disabled,
  hint,
}) => {
  const update = (i: number, patch: Partial<BodyRateRow>) =>
    onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const remove = (i: number) => onChange(rows.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p className={eyebrow}>{label}</p>
        <Button
          tone="neutral"
          style="ghost"
          size="xs"
          onClick={() => onChange([...rows, blankBodyRow()])}
          disabled={disabled}
          aria-label={`${t.addOverride} — ${label}`}
        >
          <RiAddLine />
          {t.addOverride}
        </Button>
      </div>
      {rows.length === 0 ? (
        <p className="text-[12px] text-text-soft-400">{t.noOverrides}</p>
      ) : (
        <ul className="space-y-2">
          {rows.map((row, i) => {
            const n = i + 1;
            return (
              <li
                key={`${idPrefix}-${i}`}
                className="grid grid-cols-[1fr_1fr_auto] items-end gap-2"
              >
                <Field>
                  <Label htmlFor={`${idPrefix}-key-${n}`}>{t.bodyKey}</Label>
                  <InputRoot size="sm">
                    <Input
                      className="min-w-0 w-full"
                      id={`${idPrefix}-key-${n}`}
                      value={row.key}
                      placeholder={t.bodyKeyPlaceholder}
                      disabled={disabled}
                      onChange={(e) => update(i, { key: e.target.value })}
                    />
                  </InputRoot>
                </Field>
                <Field>
                  <Label htmlFor={`${idPrefix}-rate-${n}`}>{t.bodyRate}</Label>
                  <MoneyInput
                    id={`${idPrefix}-rate-${n}`}
                    aria-label={`${t.bodyRate} ${label} ${n}`}
                    size="sm"
                    value={row.rate}
                    disabled={disabled}
                    onChange={(v) => update(i, { rate: v })}
                  />
                </Field>
                <IconButton
                  type="button"
                  size="sm"
                  tone="neutral"
                  style="ghost"
                  aria-label={`${t.removeOverride} ${row.key.trim() || n}`}
                  disabled={disabled}
                  onClick={() => remove(i)}
                >
                  <RiDeleteBinLine />
                </IconButton>
              </li>
            );
          })}
        </ul>
      )}
      {hint && <Hint>{hint}</Hint>}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// DISTANCE_TIER rows: KM ≤ + rate
// ─────────────────────────────────────────────────────────────────────────────

export interface TierEditorProps {
  rows: TierRow[];
  onChange: (rows: TierRow[]) => void;
  disabled?: boolean;
}

export const TierEditor: React.FC<TierEditorProps> = ({
  rows,
  onChange,
  disabled,
}) => {
  const update = (i: number, patch: Partial<TierRow>) =>
    onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const remove = (i: number) => onChange(rows.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p className={eyebrow}>{t.tiers}</p>
        <Button
          tone="neutral"
          style="ghost"
          size="xs"
          onClick={() => onChange([...rows, blankTierRow()])}
          disabled={disabled}
        >
          <RiAddLine />
          {t.addTier}
        </Button>
      </div>
      <ul className="space-y-2">
        {rows.map((row, i) => {
          const n = i + 1;
          return (
            <li
              key={`tier-${i}`}
              className="grid grid-cols-[1fr_1fr_auto] items-end gap-2"
            >
              <Field>
                <Label htmlFor={`tier-km-${n}`}>{t.tierMaxKm}</Label>
                <InputRoot size="sm">
                  <Input
                    id={`tier-km-${n}`}
                    type="number"
                    inputMode="decimal"
                    min={0}
                    step="0.1"
                    aria-label={`${t.tierMaxKm} tier ${n}`}
                    value={row.maxKm}
                    disabled={disabled}
                    className="min-w-0 w-full font-mono tabular-nums text-right"
                    onChange={(e) => update(i, { maxKm: e.target.value })}
                  />
                </InputRoot>
              </Field>
              <Field>
                <Label htmlFor={`tier-rate-${n}`}>{t.tierRate}</Label>
                <MoneyInput
                  id={`tier-rate-${n}`}
                  aria-label={`${t.tierRate} tier ${n}`}
                  size="sm"
                  value={row.rate}
                  disabled={disabled}
                  onChange={(v) => update(i, { rate: v })}
                />
              </Field>
              <IconButton
                type="button"
                size="sm"
                tone="neutral"
                style="ghost"
                aria-label={`${t.removeTier} ${n}`}
                disabled={disabled || rows.length <= 1}
                onClick={() => remove(i)}
              >
                <RiDeleteBinLine />
              </IconButton>
            </li>
          );
        })}
      </ul>
      <Hint>{t.tierHint}</Hint>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Multi-select code chips (hari / shift operasi)
// ─────────────────────────────────────────────────────────────────────────────

export interface CodeChipsProps<T extends string> {
  label: string;
  options: readonly T[];
  labels: Record<T, string>;
  value: T[];
  onChange: (next: T[]) => void;
  /** Shown when nothing is selected — "empty = all". */
  emptyLabel: string;
  disabled?: boolean;
}

export function CodeChips<T extends string>({
  label,
  options,
  labels,
  value,
  onChange,
  emptyLabel,
  disabled,
}: CodeChipsProps<T>) {
  return (
    <Field>
      <div className="flex items-center justify-between gap-2">
        <Label>{label}</Label>
        <span className="text-[11px] text-text-soft-400">
          {value.length === 0
            ? emptyLabel
            : value.map((code) => labels[code]).join(', ')}
        </span>
      </div>
      <ToggleGroup
        type="multiple"
        aria-label={label}
        variant="outline"
        size="sm"
        value={value}
        disabled={disabled}
        onValueChange={(next: string[]) =>
          // Keep the canonical option order regardless of click order.
          onChange(options.filter((o) => next.includes(o)))
        }
        className="flex flex-wrap justify-start gap-1.5"
      >
        {options.map((code) => (
          <ToggleGroupItem key={code} value={code} className="min-w-11 px-2">
            {labels[code]}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </Field>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Driver / helper shares (single + multi)
// ─────────────────────────────────────────────────────────────────────────────

export interface SharesFieldsProps {
  idPrefix: string;
  value: SharesDraft;
  onChange: (next: SharesDraft) => void;
  disabled?: boolean;
  size?: 'sm' | 'md';
}

export const SharesFields: React.FC<SharesFieldsProps> = ({
  idPrefix,
  value,
  onChange,
  disabled,
  size = 'md',
}) => {
  const set = (k: keyof SharesDraft) => (v: string) =>
    onChange({ ...value, [k]: v });
  const fields: { key: keyof SharesDraft; label: string }[] = [
    { key: 'driver', label: t.driverShare },
    { key: 'helper', label: t.helperShare },
    { key: 'driverMulti', label: t.driverShareMulti },
    { key: 'helperMulti', label: t.helperShareMulti },
  ];
  return (
    <div className="space-y-2">
      <p className={eyebrow}>{t.shares}</p>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {fields.map((f) => (
          <Field key={f.key}>
            <Label htmlFor={`${idPrefix}-${f.key}`}>{f.label}</Label>
            <MoneyInput
              id={`${idPrefix}-${f.key}`}
              aria-label={`${f.label} ${idPrefix}`}
              size={size}
              value={value[f.key]}
              disabled={disabled}
              onChange={set(f.key)}
            />
          </Field>
        ))}
      </div>
      <Hint>{t.sharesHint}</Hint>
    </div>
  );
};
